import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box, Card, CardActionArea, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Grid, Radio, Stack, Tooltip, Typography, Button
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ProductService from "../../../../services/product.services";
import { availabilityLabel, formatCurrency, formatDate, isPackAvailable } from "../utils";

const MembershipModal = ({ open, onClose, onConfirm }) => {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMembershipId, setSelectedMembershipId] = useState(null);
  const [selectedPack, setSelectedPack] = useState(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        setLoading(true);
        const res = await ProductService.getPack();
        setPacks(Array.isArray(res) ? res : []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  const handleSelectPack = (pack, disabled) => {
    if (disabled) return;
    const membershipId = pack?.membership?.id ?? null;
    setSelectedMembershipId(membershipId);
    setSelectedPack(pack);
  };

  const handleConfirm = () => {
    if (!selectedPack) return;
    onConfirm(selectedPack);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Asignar membresía</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            {packs.map((pack, idx) => {
              const membershipId = pack?.membership?.id ?? idx;
              const available = isPackAvailable(pack);
              const isSelectedPack = selectedMembershipId === membershipId;
              const disabledReason =
                String(pack?.product?.type || "").toLowerCase() === "disabled"
                  ? "Este pack está deshabilitado."
                  : "Este pack no está disponible en este momento.";

              return (
                <Grid item xs={12} sm={6} key={membershipId}>
                  <Tooltip
                    title={available ? "" : disabledReason}
                    placement="top"
                    arrow
                    disableHoverListener={available}
                  >
                    <Card
                      elevation={isSelectedPack ? 6 : 2}
                      sx={(theme) => ({
                        borderRadius: 3,
                        transition: "all .2s ease",
                        outline: isSelectedPack
                          ? `2px solid ${theme.palette.primary.main}`
                          : `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                        bgcolor: available ? "background.paper" : alpha("#000", 0.02),
                        opacity: available ? 1 : 0.6,
                      })}
                    >
                      <CardActionArea
                        onClick={() => handleSelectPack(pack, !available)}
                        disabled={!available}
                        sx={{ p: 1, borderRadius: 3 }}
                      >
                        <CardContent>
                          <Stack direction="row" alignItems="flex-start" spacing={1}>
                            <Radio
                              checked={isSelectedPack}
                              value={membershipId}
                              disabled={!available}
                              sx={{ mt: 0.5 }}
                              color="primary"
                            />
                            <Box flex={1}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                flexWrap="wrap"
                                gap={1}
                              >
                                <Typography variant="subtitle1" fontWeight={700}>
                                  {pack?.product?.name}
                                </Typography>
                                <Chip
                                  size="small"
                                  color={availabilityLabel(pack) === "Disponible" ? "success" : "default"}
                                  label={availabilityLabel(pack)}
                                />
                              </Stack>

                              <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                                {pack?.product?.description}
                              </Typography>

                              <Stack direction="row" spacing={2} sx={{ mt: 1 }} useFlexGap flexWrap="wrap">
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={`ARS ${formatCurrency(pack?.product?.ARG?.price, "ARS")}`}
                                />
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={`USD ${formatCurrency(pack?.product?.DOLARS?.price, "USD")}`}
                                />
                              </Stack>

                              <Stack direction="row" spacing={1.5} sx={{ mt: 1.25 }} useFlexGap flexWrap="wrap">
                                <Typography variant="caption">
                                  Vigencia: {formatDate(pack?.membership?.start)} — {formatDate(pack?.membership?.end)}
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                  {pack?.workshops?.length > 0
                                    ? `Incluye ${pack.workshops.length} taller(es)`
                                    : "Incluye todos los talleres del año"}
                                </Typography>
                              </Stack>
                            </Box>
                          </Stack>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Tooltip>
                </Grid>
              );
            })}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleConfirm}
          disabled={!selectedPack}
        >
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

MembershipModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default MembershipModal;
